import {
  computed,
  ref,
  toValue,
  watch,
  type InjectionKey,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'
import { useRouteQuery } from '@vueuse/router'

export type TabBarItem = {
  id: string
  title: string
  icon?: string
  img?: string
  badgeCount?: number
}

export type TabBarGroupContext = {
  selectedIndex: Ref<number>
}

export const tabBarGroupKey: InjectionKey<TabBarGroupContext> = Symbol('tabBarGroup')

const TAB_QUERY_PARAM = 'tab'

const queryValue = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    return value[0] ?? undefined
  }
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

const indexForId = (items: TabBarItem[], id: string | undefined) => {
  if (!id) return -1
  return items.findIndex((item) => item.id === id)
}

export const useTabBarQuery = (
  tabItems: MaybeRefOrGetter<TabBarItem[]>,
  defaultTabId?: MaybeRefOrGetter<string | undefined>,
) => {
  const tabQuery = useRouteQuery<string | undefined>(TAB_QUERY_PARAM, undefined, {
    mode: 'replace',
  })

  const items = computed(() => toValue(tabItems))

  const indexFromQuery = () => {
    const queryIndex = indexForId(items.value, queryValue(tabQuery.value))
    if (queryIndex !== -1) return queryIndex

    const defaultIndex = indexForId(items.value, toValue(defaultTabId))
    return defaultIndex === -1 ? 0 : defaultIndex
  }

  const selectedIndex = ref(indexFromQuery())

  watch([tabQuery, items, () => toValue(defaultTabId)], () => {
    selectedIndex.value = indexFromQuery()
  })

  const changeTab = (index: number) => {
    selectedIndex.value = index
    const id = items.value[index]?.id
    if (id && queryValue(tabQuery.value) !== id) {
      tabQuery.value = id
    }
  }

  return { selectedIndex, changeTab }
}
